import React, { FunctionComponent, ReactNode } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanMeasureRow } from './NpTestPlanMeasureRow'
import { NpTestPlanMeasure } from '../data/npTestPlanTypes'

export interface NpTestPlanMeasureGroupProps {
  title: string
  subtitle?: string
  /** Rendered on the right of the header, e.g. the informant's contacts and actions. */
  headerAccessory?: ReactNode
  measures: NpTestPlanMeasure[]
  footer?: ReactNode
  onRemoveMeasure?: (measure: NpTestPlanMeasure) => void
  isRemoveDisabled?: boolean
  testIdName: string
}

const Card = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  borderWidth: 1,
  borderColor: colors.borderDefault,
  borderStyle: 'solid',
  borderRadius: '16px',
  backgroundColor: colors.backgroundPrimary,
  overflow: 'hidden',
}))

const Header = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['24px'],
  minHeight: '72px',
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['16px'],
  backgroundColor: colors.backgroundSection,
}))

const HeaderTitle = styled.View({
  flex: 1,
})

const Footer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  justifyContent: 'center',
  minHeight: '72px',
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['16px'],
  borderTopWidth: 1,
  borderTopColor: colors.borderDefault,
  borderStyle: 'solid',
}))

export const NpTestPlanMeasureGroup: FunctionComponent<NpTestPlanMeasureGroupProps> = ({
  title,
  subtitle,
  headerAccessory,
  measures,
  footer,
  onRemoveMeasure,
  isRemoveDisabled,
  testIdName,
}) => {
  const { colors } = useTheme() as ThemeType

  return (
    <Card testID={tID(testIdName)}>
      <Header>
        <HeaderTitle>
          <BodyText bold size={BodyTextSize.LARGE} text={title} />
          {Boolean(subtitle) && <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} text={subtitle} />}
        </HeaderTitle>
        {headerAccessory}
      </Header>
      {measures.map((measure) => (
        <NpTestPlanMeasureRow
          key={measure.id}
          measure={measure}
          onRemove={onRemoveMeasure && (() => onRemoveMeasure(measure))}
          isRemoveDisabled={isRemoveDisabled}
        />
      ))}
      {Boolean(footer) && <Footer>{footer}</Footer>}
    </Card>
  )
}
